/**
 * 用户记忆图谱页面
 * 显示某个用户在某个项目下的记忆图谱
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Network, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { ProjectApiService } from '@/modules/agent/services/project-api'
import { memoryAdminApi } from '@/modules/memory/services/memory-admin-api'
import { useGraphStore, type GraphNode } from '@/modules/memory/store/graphStore'
import GraphHeader from '@/modules/memory/components/graph/GraphHeader'
import GraphLegend from '@/modules/memory/components/graph/GraphLegend'
import NodeTooltip from '@/modules/memory/components/graph/NodeTooltip'
import GraphCanvas2D from '@/modules/memory/components/graph/GraphCanvas2D'
import GraphCanvas3D from '@/modules/memory/components/graph/GraphCanvas3D'
import { useToast } from '@/shared/hooks/useToast'

export default function UserMemoryGraph() {
  const { id: projectId, userId } = useParams<{ id: string; userId: string }>()
  const navigate = useNavigate()
  const { showError } = useToast()

  const [projectName, setProjectName] = useState<string>('')
  const [userName, setUserName] = useState<string>('')
  const [isLoadingInfo, setIsLoadingInfo] = useState(true)
  const [infoError, setInfoError] = useState<string | null>(null)

  // 图谱状态
  const {
    isLoading,
    isInitialized,
    error,
    actualMode,
    loadInitial,
    refresh,
    reset,
    getNodesArray,
    getEdgesArray,
    userId: storeUserId,
    projectId: storeProjectId,
    // 分页加载功能可通过 useGraphStore 的 loadMore 和 hasMore* 实现
  } = useGraphStore()

  // Tooltip 状态
  const [tooltipNode, setTooltipNode] = useState<GraphNode | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [tooltipVisible, setTooltipVisible] = useState(false)

  // 加载项目和用户信息
  useEffect(() => {
    if (!projectId || !userId) {
      setInfoError('项目ID或用户ID不存在')
      setIsLoadingInfo(false)
      return
    }

    const loadInfo = async () => {
      setIsLoadingInfo(true)
      setInfoError(null)

      try {
        // 加载项目信息
        const projectResponse = await ProjectApiService.getProject(projectId)
        if (projectResponse.success && projectResponse.data) {
          setProjectName(projectResponse.data.name)
        }

        // 尝试从用户列表获取用户信息
        try {
          const users = await memoryAdminApi.getProjectUsers(projectId)
          const userInfo = users.find(u => u.user_id === userId)
          if (userInfo) {
            setUserName(userInfo.nickname || userInfo.email || userId)
          } else {
            setUserName(userId)
          }
        } catch {
          setUserName(userId)
        }
      } catch (err) {
        console.error('Failed to load info:', err)
        setInfoError('加载信息失败')
        showError('加载信息失败')
      } finally {
        setIsLoadingInfo(false)
      }
    }

    loadInfo()
  }, [projectId, userId, showError])

  // 加载图谱数据
  // 每次进入页面都检查：如果用户ID或项目ID变化了，就重新加载
  useEffect(() => {
    if (!userId) return
    
    // 检查是否需要加载：首次加载 或 用户/项目变化
    const needsLoad = !isInitialized || storeUserId !== userId || storeProjectId !== projectId
    
    if (needsLoad) {
      console.log('[UserMemoryGraph] 加载图谱数据:', { 
        userId, 
        projectId, 
        storeUserId, 
        storeProjectId,
        isInitialized,
        needsLoad 
      })
      loadInitial(userId, projectId)
    }
  }, [userId, projectId, isInitialized, storeUserId, storeProjectId, loadInitial])

  // 组件卸载时重置 store，避免缓存问题
  useEffect(() => {
    return () => {
      reset()
    }
  }, [])

  // 处理节点 hover
  const handleNodeHover = useCallback((node: GraphNode | null, position: { x: number; y: number }) => {
    setTooltipNode(node)
    setTooltipPosition(position)
    setTooltipVisible(!!node)
  }, [])

  // 处理节点点击
  const handleNodeClick = useCallback(() => {
    // 暂不处理节点点击
  }, [])

  // 处理刷新
  const handleRefresh = useCallback(() => {
    refresh()
  }, [refresh])

  // 加载更多功能通过 loadMore 回调提供

  if (isLoadingInfo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">加载中...</p>
        </div>
      </div>
    )
  }

  if (infoError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              加载失败
            </CardTitle>
            <CardDescription>{infoError}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button onClick={() => window.location.reload()} variant="outline">
                重试
              </Button>
              <Button onClick={() => navigate(`/project/${projectId}/memory`)} variant="ghost">
                返回列表
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const nodes = getNodesArray()
  const edges = getEdgesArray()

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* 顶部导航栏 */}
      <header className="border-b bg-card sticky top-0 z-10 shrink-0">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/project/${projectId}/memory`}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Network className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl tracking-tight">
                {userName} 的记忆图谱
              </span>
              {projectName && (
                <span className="text-muted-foreground text-sm">
                  ({projectName})
                </span>
              )}
            </div>
          </div>
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="sm" 
            className="gap-2"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>
      </header>

      {/* 图谱主体 */}
      <div className="flex-1 relative">
        {/* 加载状态 */}
        {isLoading && !isInitialized && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground">加载图谱数据...</p>
            </div>
          </div>
        )}

        {/* 错误状态 */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
            <Card className="max-w-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive text-base">
                  <AlertCircle className="h-4 w-4" />
                  加载失败
                </CardTitle>
                <CardDescription>{error}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleRefresh} variant="outline" size="sm">
                  重试
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 空状态 */}
        {!isLoading && !error && nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Network className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-muted-foreground">暂无记忆数据</p>
              <p className="text-sm text-muted-foreground/60 mt-1">
                与该用户对话后，记忆将自动提取并显示在这里
              </p>
            </div>
          </div>
        )}

        {/* 图谱画布 */}
        {nodes.length > 0 && (
          <>
            <GraphHeader
              onRefresh={handleRefresh}
              title={`${userName} 的记忆图谱`}
            />

            {actualMode === '3d' ? (
              <GraphCanvas3D
                nodes={nodes}
                edges={edges}
                onNodeHover={handleNodeHover}
                onNodeClick={handleNodeClick}
              />
            ) : (
              <GraphCanvas2D
                nodes={nodes}
                edges={edges}
                onNodeHover={handleNodeHover}
                onNodeClick={handleNodeClick}
              />
            )}

            <GraphLegend />

            {tooltipVisible && tooltipNode && (
              <NodeTooltip
                node={tooltipNode}
                position={tooltipPosition}
                visible={tooltipVisible}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

